import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useOutletContext, useParams } from 'react-router-dom';

import useFeatureFlags from 'api/useFeatureFlags';
import useSections from 'api/useSections';
import useStoreSettings, {
  StoreSettingsValues,
  useStoreSettingsSectionMutation
} from 'api/useStoreSettings';
import useTabs from 'api/useTabs';
import useTheme from 'api/useTheme';
import { AxiosError } from 'axios';
import {
  EBundlesInternalViewModel,
  EFeatureFlag,
  ESectionOfferType
} from 'constants/enums';

import { EToasterStatus, showToast } from '../../../utils/showToast';
import { CollapsibleSectionForm } from '../../CollapsibleSectionForm';
import { defaultGeneralTheme } from '../GeneralSettings/defaults';
import { defaultStoreTheme } from '../StoreTheme/defaults';
import { SectionsItem, TabsItem } from '../StoreTheme/storeTheme.types';
import { ThemeLoader } from '../ThemeLoader';

import { EmptyStateSectionFields } from './components/EmptyStateSectionFields';
import { FooterSectionFields } from './components/FooterSectionFields';
import { GeneralEssentialsSectionFields } from './components/GeneralEssentialsSectionFields';
import { MaintenanceSectionFields } from './components/MaintenanceSectionFields';
import { SectionsSectionFields } from './components/SectionsSectionFields';
import { SEOSectionFields } from './components/SEOSectionFields';
import { TabsSectionFields } from './components/TabsSectionFields';
import type {
  EmptyStateFormValues,
  FooterFormValues,
  GeneralEssentialsFormValues,
  MaintenanceFormValues,
  SEOFormValues,
  StoreSettingsOutletContext
} from './storeSettingsView.types';
import {
  emptyStateSchema,
  footerSchema,
  generalEssentialsSchema,
  getSeoSchema,
  maintenanceSchema,
  storeSectionsSchema,
  storeTabsSchema
} from './validation-schemas';

import 'style/forms.scss';

const saveErrorToast = (error: unknown, fallbackMessage: string) => {
  showToast({
    message: fallbackMessage,
    status: EToasterStatus.ERROR,
    description:
      ((error as AxiosError).response?.data as { message?: string })?.message ||
      'Unknown error occurred'
  });
};

export const StoreSettingsView: FC = () => {
  const { publisherId } = useParams();
  const outletContext = useOutletContext<StoreSettingsOutletContext | undefined>();
  const { getStoreSettings } = useStoreSettings(publisherId);
  const generalMutation = useStoreSettingsSectionMutation(publisherId);
  const footerMutation = useStoreSettingsSectionMutation(publisherId);
  const emptyStateMutation = useStoreSettingsSectionMutation(publisherId);
  const seoMutation = useStoreSettingsSectionMutation(publisherId);
  const maintenanceMutation = useStoreSettingsSectionMutation(publisherId);
  const { getSections, upsertSections } = useSections(publisherId);
  const { getTabs, upsertTabs } = useTabs(publisherId);
  const { featureFlags } = useFeatureFlags({ publisherId });
  const { getGeneralTheme: generalTheme } = useTheme(publisherId);
  const storeSettingsData = getStoreSettings.data;

  const isProductMadness = useMemo(() => {
    const bundleViewModel = generalTheme?.data?.bundlesInternalViewModel;
    if (!bundleViewModel) return false;
    return [
      EBundlesInternalViewModel.JACKPOT_SLOTS,
      EBundlesInternalViewModel.BIG_FISH_CASINO
    ].includes(bundleViewModel);
  }, [generalTheme.data?.bundlesInternalViewModel]);
  const isStoreSEOEnabled =
    !!featureFlags?.[EFeatureFlag.DASHBOARD_STORE_SEO_SETTINGS];
  const enableTabs = useMemo(
    () => featureFlags?.[EFeatureFlag.STORE_TABS],
    [featureFlags]
  );
  const sectionsDirtyRef = useRef<Record<string, boolean>>({});
  const aggregateDirtyRef = useRef(false);
  const [sectionsDraftActive, setSectionsDraftActive] = useState(false);
  const [tabsDraftActive, setTabsDraftActive] = useState(false);

  const handleSectionDirtyChange = useCallback(
    (sectionKey: string, isDirty: boolean) => {
      const prev = sectionsDirtyRef.current;
      if (prev[sectionKey] === isDirty) return;
      sectionsDirtyRef.current = { ...prev, [sectionKey]: isDirty };
      const nextAggregate = Object.values(sectionsDirtyRef.current).some(Boolean);
      if (nextAggregate !== aggregateDirtyRef.current) {
        aggregateDirtyRef.current = nextAggregate;
        outletContext?.setStoreSettingsDirty?.(nextAggregate);
      }
    },
    [outletContext]
  );

  useEffect(() => {
    return () => {
      outletContext?.setStoreSettingsDirty?.(false);
    };
  }, [outletContext]);

  const normalizeFooterForForm = (
    footer?: StoreSettingsValues['footer']
  ): FooterFormValues => {
    const defaultSupportParams = defaultGeneralTheme.footer.supportParams ?? {};
    const supportParams = footer?.supportParams;

    return {
      ...defaultGeneralTheme.footer,
      ...(footer ?? {}),
      supportParams: {
        ...defaultSupportParams,
        ...(supportParams ?? {}),
        playerNameParamName:
          supportParams?.playerNameParamName ??
          supportParams?.playerEmailParamName ??
          defaultSupportParams.playerNameParamName
      }
    };
  };

  const normalizeFooterForApi = (
    footer: FooterFormValues
  ): NonNullable<StoreSettingsValues['footer']> => {
    const supportParams = footer.supportParams;
    if (!supportParams) {
      return footer;
    }

    const { playerNameParamName, ...restSupportParams } = supportParams;

    return {
      ...footer,
      supportParams: {
        ...restSupportParams,
        ...(playerNameParamName !== undefined
          ? { playerEmailParamName: playerNameParamName }
          : {})
      }
    };
  };

  const saveSectionSettings = async (
    mutation: ReturnType<typeof useStoreSettingsSectionMutation>,
    payload: Partial<StoreSettingsValues>,
    successMessage: string,
    fallbackMessage: string
  ) => {
    try {
      await mutation.mutateAsync(payload);
      showToast({
        message: successMessage,
        status: EToasterStatus.SUCCESS
      });
    } catch (error) {
      saveErrorToast(error, fallbackMessage);
      throw error;
    }
  };

  const footerInitialValues = useMemo(
    () => normalizeFooterForForm(storeSettingsData?.footer),
    [storeSettingsData?.footer]
  );

  const generalEssentialsInitialValues = useMemo(
    () => ({
      storeTabName:
        storeSettingsData?.storeTabName ?? defaultGeneralTheme.storeTabName,
      loaderAnimation:
        storeSettingsData?.loaderAnimation ?? defaultGeneralTheme.loaderAnimation,
      defaultPlayerImage:
        storeSettingsData?.defaultPlayerImage ?? defaultStoreTheme.defaultPlayerImage,
      favicon: storeSettingsData?.favicon ?? defaultGeneralTheme.favicon,
      storeSaleImage:
        storeSettingsData?.storeSaleImage ?? defaultGeneralTheme.storeSaleImage
    }),
    [
      storeSettingsData?.storeTabName,
      storeSettingsData?.loaderAnimation,
      storeSettingsData?.favicon,
      storeSettingsData?.defaultPlayerImage,
      storeSettingsData?.storeSaleImage
    ]
  );

  const emptyStateInitialValues = useMemo(
    () => ({
      noOffersTitleText:
        storeSettingsData?.emptyState?.noOffersTitleText ??
        defaultStoreTheme.noOffersTitleText,
      noOffersMessageText:
        storeSettingsData?.emptyState?.noOffersMessageText ??
        defaultStoreTheme.noOffersMessageText
    }),
    [
      storeSettingsData?.emptyState?.noOffersTitleText,
      storeSettingsData?.emptyState?.noOffersMessageText
    ]
  );

  const seoInitialValues = useMemo(
    () => ({
      ...(storeSettingsData?.seo ?? defaultStoreTheme.seo)
    }),
    [storeSettingsData?.seo]
  );

  const maintenanceInitialValues = useMemo(
    () => ({
      maintenanceModeRichText:
        storeSettingsData?.maintenanceModeRichText ??
        defaultGeneralTheme.maintenanceModeRichText
    }),
    [storeSettingsData?.maintenanceModeRichText]
  );

  const sectionsInitialValues = useMemo(
    () => ({
      bundleSections:
        getSections.data?.result.filter(
          (section: SectionsItem) =>
            !section.offerType || section.offerType === ESectionOfferType.BUNDLE
        ) ?? defaultStoreTheme.bundleSections,
      promotionSections:
        getSections.data?.result.filter(
          (section: SectionsItem) =>
            section.offerType === ESectionOfferType.PROMOTION
        ) ?? defaultStoreTheme.promotionSections
    }),
    [getSections.data?.result]
  );

  const tabsInitialValues = useMemo(
    () => ({
      tabs:
        (getTabs.data || defaultStoreTheme.tabs).map((tab: TabsItem) => ({
          ...tab,
          _id: tab.publisherTabId || tab.name
        })) ?? []
    }),
    [getTabs.data]
  );

  const handleGeneralSettingsSubmit = async (
    values: GeneralEssentialsFormValues
  ) => {
    await saveSectionSettings(
      generalMutation,
      values as Partial<StoreSettingsValues>,
      'General settings updated',
      'Error updating general settings'
    );
  };

  const handleFooterSubmit = async (values: FooterFormValues) => {
    await saveSectionSettings(
      footerMutation,
      { footer: normalizeFooterForApi(values) },
      'Footer updated',
      'Error updating footer'
    );
  };

  const handleSectionsSubmit = async (values: typeof sectionsInitialValues) => {
    try {
      const newSectionsData = values.bundleSections
        .concat(values.promotionSections)
        .map((value: SectionsItem) => {
          const { _id, ...rest } = value;
          return rest;
        })
        .filter((item: SectionsItem) => !item.toBeDeleted)
        .map((section: SectionsItem) => {
          const sectionData: Partial<SectionsItem> = {
            name: section.name,
            publisherSectionId: section.publisherSectionId,
            offerType: section.offerType || ESectionOfferType.BUNDLE
          };
          if (section.image !== undefined) {
            sectionData.image = section.image;
          }
          if (section.sectionId) {
            sectionData.sectionId = section.sectionId;
          }
          return sectionData;
        });

      await upsertSections.mutateAsync(newSectionsData);
      await getSections.refetch();
      showToast({
        message: 'Sections updated',
        status: EToasterStatus.SUCCESS
      });
    } catch (error) {
      saveErrorToast(error, 'Error updating sections');
      throw error;
    }
  };

  const handleTabsSubmit = async (values: typeof tabsInitialValues) => {
    try {
      const newTabsData = (values.tabs || [])
        .map((value: TabsItem) => {
          const { _id, ...rest } = value;
          return rest;
        })
        .filter((item: TabsItem) => !item.toBeDeleted)
        .map((tab: TabsItem) => {
          const tabData: Partial<TabsItem> = {
            name: tab.name,
            publisherTabId: tab.publisherTabId
          };
          if (tab.iconUrl !== undefined) {
            tabData.iconUrl = tab.iconUrl;
          }
          if (tab.tabId) {
            tabData.tabId = tab.tabId;
          }
          return tabData;
        });

      await upsertTabs.mutateAsync(newTabsData);
      await getTabs.refetch();
      showToast({
        message: 'Tabs updated',
        status: EToasterStatus.SUCCESS
      });
    } catch (error) {
      saveErrorToast(error, 'Error updating tabs');
      throw error;
    }
  };

  const handleEmptyStateSubmit = async (
    values: EmptyStateFormValues
  ) => {
    await saveSectionSettings(
      emptyStateMutation,
      { emptyState: values },
      'Empty state updated',
      'Error updating empty state'
    );
  };

  const handleSeoSubmit = async (values: SEOFormValues) => {
    await saveSectionSettings(
      seoMutation,
      { seo: values },
      'SEO settings updated',
      'Error updating SEO settings'
    );
  };

  const handleMaintenanceSubmit = async (
    values: MaintenanceFormValues
  ) => {
    await saveSectionSettings(
      maintenanceMutation,
      values as Partial<StoreSettingsValues>,
      'Maintenance settings updated',
      'Error updating maintenance settings'
    );
  };

  const isLoading =
    getStoreSettings.isLoading ||
    !getStoreSettings.data ||
    getSections.isLoading ||
    getTabs.isLoading;

  if (isLoading) {
    return <ThemeLoader />;
  }

  return (
    <div className="w-full space-y-4 bg-base-muted/30">
      <CollapsibleSectionForm
        title="General Settings"
        description="Configure your store's tab title, loader animation, default player profile and favicon."
        defaultExpanded={false}
        initialValues={generalEssentialsInitialValues}
        validationSchema={generalEssentialsSchema}
        isLoading={generalMutation.isLoading}
        enableReinitialize={false}
        onDirtyChange={(dirty) =>
          handleSectionDirtyChange('generalEssentials', dirty)
        }
        onSubmit={handleGeneralSettingsSubmit}
      >
        <GeneralEssentialsSectionFields isProductMadness={isProductMadness} />
      </CollapsibleSectionForm>

      <CollapsibleSectionForm
        title="Footer"
        description="Enjoy a seamless, secure experience designed to keep you connected and informed."
        defaultExpanded={false}
        initialValues={footerInitialValues}
        validationSchema={footerSchema}
        isLoading={footerMutation.isLoading}
        enableReinitialize={false}
        onDirtyChange={(dirty) => handleSectionDirtyChange('footer', dirty)}
        onSubmit={handleFooterSubmit}
      >
        <FooterSectionFields />
      </CollapsibleSectionForm>

      <CollapsibleSectionForm
        title="Sections"
        description="Organize your store offers into sections."
        defaultExpanded={false}
        initialValues={sectionsInitialValues}
        validationSchema={storeSectionsSchema}
        isLoading={upsertSections.isLoading}
        onDirtyChange={(dirty) => handleSectionDirtyChange('sections', dirty)}
        onSubmit={handleSectionsSubmit}
        enableReinitialize={false}
        isExternallyDirty={sectionsDraftActive}
      >
        <SectionsSectionFields onDraftChange={setSectionsDraftActive} />
      </CollapsibleSectionForm>

      {enableTabs && (
        <CollapsibleSectionForm
          title="Tabs"
          description="Organize your store offers across multiple tabs."
          defaultExpanded={false}
          initialValues={tabsInitialValues}
          validationSchema={storeTabsSchema}
          isLoading={upsertTabs.isLoading}
          onDirtyChange={(dirty) => handleSectionDirtyChange('tabs', dirty)}
          onSubmit={handleTabsSubmit}
          enableReinitialize={false}
          isExternallyDirty={tabsDraftActive}
        >
          <TabsSectionFields onDraftChange={setTabsDraftActive} />
        </CollapsibleSectionForm>
      )}

      <CollapsibleSectionForm
        title="Empty State - No Offers"
        description="Customize the message players will see when no offers are available in the store."
        defaultExpanded={false}
        initialValues={emptyStateInitialValues}
        validationSchema={emptyStateSchema}
        isLoading={emptyStateMutation.isLoading}
        enableReinitialize={false}
        onDirtyChange={(dirty) => handleSectionDirtyChange('emptyState', dirty)}
        onSubmit={handleEmptyStateSubmit}
      >
        <EmptyStateSectionFields />
      </CollapsibleSectionForm>

      {isStoreSEOEnabled && (
        <CollapsibleSectionForm
          title="SEO Settings"
          description="Customize how this page appears in search results and social media previews."
          defaultExpanded={false}
          initialValues={seoInitialValues}
          validationSchema={getSeoSchema(isStoreSEOEnabled)}
          isLoading={seoMutation.isLoading}
          enableReinitialize={false}
          onDirtyChange={(dirty) => handleSectionDirtyChange('seo', dirty)}
          onSubmit={handleSeoSubmit}
        >
          <SEOSectionFields />
        </CollapsibleSectionForm>
      )}

      <CollapsibleSectionForm
        title="Maintenance"
        description="Let users know when your project is unavailable."
        defaultExpanded={false}
        initialValues={maintenanceInitialValues}
        validationSchema={maintenanceSchema}
        isLoading={maintenanceMutation.isLoading}
        enableReinitialize={false}
        onDirtyChange={(dirty) => handleSectionDirtyChange('maintenance', dirty)}
        onSubmit={handleMaintenanceSubmit}
      >
        <MaintenanceSectionFields />
      </CollapsibleSectionForm>
    </div>
  );
};
